import { LogIn, LayoutDashboard, FilePlus2, ShieldCheck, CircleHelp, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { useLang } from "../i18n/LanguageContext";

// Guide VENDEUR (demande du propriétaire) : petite page d'aide publique qui
// explique aux vendeurs comment se connecter et utiliser leurs sections.
// Bilingue FR/EN (demande du propriétaire : vendeurs anglophones).
function contenu(lang) {
  const fr = lang !== "en";
  const L = (a, b) => (fr ? a : b);
  return {
    seo: L("Guide du vendeur | Mr Trophée", "Salesperson guide | Mr Trophée"),
    surtitre: L("Espace vendeur", "Sales area"),
    titreA: L("Guide du ", ""),
    titreB: L("vendeur", "Salesperson guide"),
    intro: L(
      "Cinq minutes suffisent : connexion, sections, factures, sécurité. Ce guide s'adresse aux vendeurs nommés par le propriétaire de Mr Trophée.",
      "Five minutes is all it takes: logging in, sections, invoices, security. This guide is for salespeople named by the owner of Mr Trophée."),
    etapes: [
      {
        icon: LogIn,
        titre: L("1. Connectez-vous", "1. Log in"),
        texte: fr
          ? <>Rendez-vous à la page <Link to="/connexion" className="text-gold hover:underline">Connexion</Link> et entrez
            votre courriel et votre mot de passe habituels (ceux de votre compte client Mr Trophée). Vous arrivez
            directement dans le tableau de bord — inutile de chercher un lien spécial.</>
          : <>Go to the <Link to="/connexion" className="text-gold hover:underline">Login</Link> page and enter
            your usual email and password (the ones from your Mr Trophée customer account). You will land
            directly in the dashboard — no special link needed.</>,
      },
      {
        icon: LayoutDashboard,
        titre: L("2. Vos sections, rien d'autre", "2. Your sections, nothing else"),
        texte: fr
          ? <>Le tableau de bord n'affiche que les sections que le propriétaire a cochées pour vous
            (ex. Ventes &amp; Soumissions, Clients, Commandes). Chaque section est en <strong>lecture seule</strong> ou
            en <strong>lecture + modification</strong> — une section en lecture seule refuse l'enregistrement, c'est normal.
            Il vous manque un accès ? Écrivez à <a href="mailto:info@trophee.ca" className="text-gold hover:underline">info@trophee.ca</a> :
            le propriétaire peut l'ouvrir en un clic, et vous recevrez un courriel de confirmation.</>
          : <>The dashboard only shows the sections the owner has checked for you
            (e.g. Sales &amp; Quotes, Customers, Orders). Each section is either <strong>read-only</strong> or
            <strong> read + edit</strong> — a read-only section will refuse to save; that's normal.
            Missing an access? Email <a href="mailto:info@trophee.ca" className="text-gold hover:underline">info@trophee.ca</a>:
            the owner can open it in one click, and you'll receive a confirmation email.</>,
      },
      {
        icon: FilePlus2,
        titre: L("3. Créer une facture ou une soumission", "3. Creating an invoice or quote"),
        texte: fr
          ? <>Si la case « Créer des factures » est cochée pour vous, votre portail
            <Link to="/vendeur" className="text-gold hover:underline"> /vendeur</Link> permet de créer une facture-soumission
            pour un client : nom, courriel, articles (nom, quantité, prix). Les taxes TPS/TVQ sont calculées
            automatiquement et le document arrive dans « Ventes &amp; Soumissions » de l'admin.</>
          : <>If the "Create invoices" box is checked for you, your
            <Link to="/vendeur" className="text-gold hover:underline"> /vendeur</Link> portal lets you create an
            invoice-quote for a customer: name, email, items (name, quantity, price). GST/QST taxes are calculated
            automatically and the document lands in the admin's "Sales &amp; Quotes".</>,
      },
      {
        icon: ShieldCheck,
        titre: L("4. Activez la double vérification (2FA)", "4. Turn on two-factor authentication (2FA)"),
        texte: fr
          ? <>La double vérification devient <strong>obligatoire pour les vendeurs le 22 octobre 2026</strong>.
            Ouvrez l'onglet « Paramètres » du tableau de bord, carte « Double vérification », et scannez le code avec
            votre application d'authentification (Google Authenticator, 1Password…). Faites-le dès maintenant pour
            éviter tout blocage à la connexion.</>
          : <>Two-factor authentication becomes <strong>mandatory for salespeople on October 22, 2026</strong>.
            Open the "Settings" tab in the dashboard, "Two-factor authentication" card, and scan the code with
            your authenticator app (Google Authenticator, 1Password…). Do it now to avoid any login lockout.</>,
      },
      {
        icon: CircleHelp,
        titre: L("5. Un souci de connexion ?", "5. Trouble logging in?"),
        texte: fr
          ? <>« Accès réservé au personnel » après la connexion ? Rechargez complètement la page
            (Ctrl+Maj+R) pour obtenir la dernière version du site. Mot de passe oublié : utilisez
            <Link to="/connexion" className="text-gold hover:underline"> « Mot de passe oublié »</Link> sur la page de
            connexion. Après 5 mauvais essais, le compte se verrouille 15 minutes — patientez, puis réessayez.</>
          : <>"Staff only" after logging in? Fully reload the page (Ctrl+Shift+R) to get the latest version
            of the site. Forgotten password: use <Link to="/connexion" className="text-gold hover:underline">"Forgot password"</Link> on
            the login page. After 5 failed attempts, the account locks for 15 minutes — wait, then try again.</>,
      },
    ],
    contact: fr
      ? <>Une question, un accès à ajuster ? Écrivez à
        <a href="mailto:info@trophee.ca" className="text-gold hover:underline"> info@trophee.ca</a> —
        le propriétaire règle vos cases en un clic et vous recevez un courriel de confirmation.</>
      : <>A question, an access to adjust? Email
        <a href="mailto:info@trophee.ca" className="text-gold hover:underline"> info@trophee.ca</a> —
        the owner adjusts your checkboxes in one click and you receive a confirmation email.</>,
  };
}

export default function GuideVendeur() {
  const { lang } = useLang();
  const c = contenu(lang);
  return (
    <div data-testid="guide-vendeur" className="max-w-3xl mx-auto px-6 lg:px-10 pt-32 pb-28">
      <SEO title={c.seo} path="/guide-vendeur" noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{c.surtitre}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mb-4">{c.titreA}<span className="text-gold italic">{c.titreB}</span></h1>
        <p className="text-ivory/60 text-base mb-12">{c.intro}</p>
      </FadeUp>
      <div className="space-y-6">
        {c.etapes.map((e) => (
          <FadeUp key={e.titre}>
            <div className="bg-ink border border-ivory/10 rounded-xl p-6" data-testid={`guide-etape-${e.titre.slice(0, 1)}`}>
              <h2 className="font-serif text-xl mb-2 flex items-center gap-3">
                <e.icon className="w-5 h-5 text-gold shrink-0" /> {e.titre}
              </h2>
              <p className="text-ivory/70 text-sm leading-relaxed">{e.texte}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp>
        <div className="mt-10 border border-gold/25 rounded-xl p-6 text-sm text-ivory/70 flex items-start gap-3"
          data-testid="guide-contact">
          <Mail className="w-5 h-5 text-gold shrink-0 mt-0.5" />
          <p>{c.contact}</p>
        </div>
      </FadeUp>
    </div>
  );
}
